import { z } from 'zod'
import { AppError } from '@/shared/domain/AppError'

const commonEnvironmentSchema = z.object({
  VITE_APP_ENV: z.enum(['local', 'preview', 'staging', 'production']).default('local'),
  VITE_DATA_SOURCE: z.enum(['fixtures', 'supabase']).default('fixtures'),
  VITE_MAPBOX_TOKEN: z.string().min(1).optional(),
  VITE_GOOGLE_MAPS_API_KEY: z.string().min(1).optional(),
  VITE_ENABLE_LEGACY_CLIENT_READS: z.enum(['true', 'false']).default('false'),
  VITE_PHASE2_FEEDBACK_ENABLED: z.enum(['true', 'false']).default('false'),
  // Ajustement IA des drapeaux (Routes) : désactivé par défaut depuis le 12 oct. 2026 (décision du propriétaire).
  VITE_ROUTES_AI_FLAGS_ENABLED: z.enum(['true', 'false']).default('false'),
  VITE_APP_VERSION: z.string().min(1).max(80).optional(),
  VITE_DEFAULT_AUTHENTICATED_PATH: z.enum(['/vue-ensemble', '/operations', '/feedback']).optional(),
})

const supabaseEnvironmentSchema = z.object({
  VITE_SUPABASE_URL: z.url(),
  VITE_SUPABASE_ANON_KEY: z.string().min(1),
})

export type RuntimeConfig =
  | {
      appEnvironment: 'local' | 'preview' | 'staging' | 'production'
      dataSource: 'fixtures'
      mapboxToken?: string
      googleMapsApiKey?: string
      enableLegacyClientReads?: true
      phase2FeedbackEnabled?: true
      routesAiFlagsEnabled?: true
      appVersion?: string
      defaultAuthenticatedPath?: '/vue-ensemble' | '/operations' | '/feedback'
    }
  | {
      appEnvironment: 'local' | 'preview' | 'staging' | 'production'
      dataSource: 'supabase'
      mapboxToken?: string
      googleMapsApiKey?: string
      enableLegacyClientReads?: true
      phase2FeedbackEnabled?: true
      routesAiFlagsEnabled?: true
      appVersion?: string
      defaultAuthenticatedPath?: '/vue-ensemble' | '/operations' | '/feedback'
      supabaseUrl: string
      supabaseAnonKey: string
    }

type EnvironmentInput = Record<string, string | boolean | undefined>

export function parseEnvironment(input: EnvironmentInput): RuntimeConfig {
  const commonResult = commonEnvironmentSchema.safeParse(input)

  if (!commonResult.success) {
    throw new AppError('LOCAL_CONFIGURATION_INVALID', 'La configuration locale est invalide.', {
      issues: commonResult.error.issues.map((issue) => issue.path.join('.')),
    })
  }

  const common = commonResult.data
  const sharedConfig = {
    appEnvironment: common.VITE_APP_ENV,
    ...(common.VITE_MAPBOX_TOKEN === undefined ? {} : { mapboxToken: common.VITE_MAPBOX_TOKEN }),
    ...(common.VITE_GOOGLE_MAPS_API_KEY === undefined
      ? {}
      : { googleMapsApiKey: common.VITE_GOOGLE_MAPS_API_KEY }),
    ...(common.VITE_ENABLE_LEGACY_CLIENT_READS === 'true' ? { enableLegacyClientReads: true as const } : {}),
    ...(common.VITE_PHASE2_FEEDBACK_ENABLED === 'true' ? { phase2FeedbackEnabled: true as const } : {}),
    ...(common.VITE_ROUTES_AI_FLAGS_ENABLED === 'true' ? { routesAiFlagsEnabled: true as const } : {}),
    ...(common.VITE_APP_VERSION === undefined ? {} : { appVersion: common.VITE_APP_VERSION }),
    ...(common.VITE_DEFAULT_AUTHENTICATED_PATH === undefined
      ? {}
      : { defaultAuthenticatedPath: common.VITE_DEFAULT_AUTHENTICATED_PATH }),
  }

  if (common.VITE_DATA_SOURCE === 'fixtures') {
    return {
      ...sharedConfig,
      dataSource: 'fixtures',
    }
  }

  const supabaseResult = supabaseEnvironmentSchema.safeParse(input)

  if (!supabaseResult.success) {
    throw new AppError(
      'LOCAL_CONFIGURATION_INVALID',
      'Les variables Supabase sont requises lorsque VITE_DATA_SOURCE=supabase.',
      { issues: supabaseResult.error.issues.map((issue) => issue.path.join('.')) },
    )
  }

  return {
    ...sharedConfig,
    dataSource: 'supabase',
    supabaseUrl: supabaseResult.data.VITE_SUPABASE_URL,
    supabaseAnonKey: supabaseResult.data.VITE_SUPABASE_ANON_KEY,
  }
}

export const runtimeConfig = parseEnvironment(import.meta.env)
export const authenticatedHomePath = runtimeConfig.defaultAuthenticatedPath ?? '/vue-ensemble'
